import { numberFormatCurrency } from './helpers'
import { downloadImage, downloadZipFile } from './download'
import { paymentMethods, SERVER_DATA } from '../core/constants/app'

/**
 * Convert panel names based on vehicle class (truck/suv/car)
 * Trucks use "LT Bedside/RT Bedside/Tailgate" instead of "LT Quarter/RT Quarter/Trunk"
 * SUVs use "Hatch" instead of "Trunk"
 * @param {string} name - The panel name
 * @param {string} side - Optional side (left/right)
 * @param {string} clas - Vehicle class (truck/suv/car)
 * @returns {string} - Unified panel name
 */
export const unifyPanelName = (name, side = '', clas = '') => {
	if (!name) return name
	clas = clas ? clas.toLowerCase() : ''
	const panels = {
		'partial front': 'Partial Front',
		'full front': 'Full Front',
		'track package': 'Track Package',
		'full coverage': 'Full Coverage',
		'lt cab corner': 'LT Cab Corner',
		'rt cab corner': 'RT Cab Corner',
		'front bumper': 'Front Bumper',
		'rear bumper': 'Rear Bumper',
		'dashboard': 'Dashboard',
		'headlights': 'Headlights',
		'cargo area': 'Cargo Area',
		'headliner': 'Headliner',
		'carpet': 'Carpet',
		'floor mats': 'Floor Mats',
		'lt a-pillar': 'LT A-Pillar',
		'lt b-pillar': 'LT B-Pillar',
		'lt c-pillar': 'LT C-Pillar',
		'rt a-pillar': 'RT A-Pillar',
		'rt b-pillar': 'RT B-Pillar',
		'rt c-pillar': 'RT C-Pillar',
		'side mirrors': 'Side Mirrors',
		'fog lights': 'Fog Lights',
		'entire vehicle': 'Entire Vehicle',
		'lff': 'LT Fender',
		'steering wheel': 'Steering Wheel',
		'cowl': 'Cowl',
		'lt fender': 'LT Fender',
		'rff': 'RT Fender',
		'rt fender': 'RT Fender',
		'lfd': 'LT Front Door',
		'lt front door': 'LT Front Door',
		'lt front kickplate': 'LT Front Kickplate',
		'lt front door panel': 'LT Front Door Panel',
		'lt front seat': 'LT Front Seat',
		'lt rear door panel': 'LT Rear Door Panel',
		'rfd': 'RT Front Door',
		'rt front door': 'RT Front Door',
		'rt front kickplate': 'RT Front Kickplate',
		'rt front door panel': 'RT Front Door Panel',
		'rt front seat': 'RT Front Seat',
		'rt rear door panel': 'RT Rear Door Panel',
		'rrd': 'RT Rear Door',
		'rt rear door': 'RT Rear Door',
		'rt rear kickplate': 'RT Rear Kickplate',
		'lrd': 'LT Rear Door',
		'lt rear door': 'LT Rear Door',
		'lt rear kickplate': 'LT Rear Kickplate',
		'fenders': side === 'right' ? 'RT Fender' : 'LT Fender',
		'fd': side === 'right' ? 'RT Front Door' : 'LT Front Door',
		'rd': side === 'right' ? 'RT Rear Door' : 'LT Rear Door',
		'q': side === 'right' ? (clas === 'truck' ? 'RT Bedside' : 'RT Quarter') : (clas === 'truck' ? 'LT Bedside' : 'LT Quarter'),
		'lq': clas === 'truck' ? 'LT Bedside' : 'LT Quarter',
		'lt quarter': clas === 'truck' ? 'LT Bedside' : 'LT Quarter',
		'center console': 'Center Console',
		'second row seat': 'Second Row Seat',
		'third row seat': 'Third Row Seat',
		'rq': clas === 'truck' ? 'RT Bedside' : 'RT Quarter',
		'rt quarter': clas === 'truck' ? 'RT Bedside' : 'RT Quarter',
		'lwing': 'LT Wing',
		'lt wing': 'LT Wing',
		'windshield': 'Windshield',
		'ws': 'Windshield',
		'sun roof': 'Sun Roof',
		'sr': 'Sun Roof',
		'back glass': 'Back Glass',
		'bg': 'Back Glass',
		'quarter panels/bedsides': side ? (side === 'right' ? (clas === 'truck' ? 'RT Bedside' : 'RT Quarter') : (clas === 'truck' ? 'LT Bedside' : 'LT Quarter')) : 'Quarter Panels',
		'quarters': side ? (side === 'right' ? (clas === 'truck' ? 'RT Bedside' : 'RT Quarter') : (clas === 'truck' ? 'LT Bedside' : 'LT Quarter')) : 'Quarters',
		'quarter': side ? (side === 'right' ? (clas === 'truck' ? 'RT Bedside' : 'RT Quarter') : (clas === 'truck' ? 'LT Bedside' : 'LT Quarter')) : 'Quarter Panels',
		'front doors': side ? (side === 'right' ? 'RT Front Door' : 'LT Front Door') : 'Front Doors',
		'rear doors': side ? (side === 'right' ? 'RT Rear Door' : 'LT Rear Door') : 'Rear Doors',
		'front door': side ? (side === 'right' ? 'RT Front Door' : 'LT Front Door') : 'Front Door',
		'rear door': side ? (side === 'right' ? 'RT Rear Door' : 'LT Rear Door') : 'Rear Door',
		'rwing': 'RT Wing',
		'rt wing': 'RT Wing',
		'lrail': 'LT Roof Rail',
		'lt roof rail': 'LT Roof Rail',
		'rrail': 'RT Roof Rail',
		'rt roof rail': 'RT Roof Rail',
		'hood': 'Hood',
		'hatch': 'Hatch',
		'tailgate': 'Tailgate',
		'roof': 'Roof',
		'trunk': clas === 'truck' ? 'Tailgate' : clas === 'suv' ? 'Hatch' : 'Trunk',
		'l bedside': 'LT Bedside',
		'lt bedside': 'LT Bedside',
		'lt fenders': 'LT Fender',
		'rt fenders': 'RT Fender',
		'rt bedside': 'RT Bedside',
		'r bedside': 'RT Bedside',
	}
	return panels[name.toLowerCase()] || name.toUpperCase()
}

export const discountPercent = record => {
	const { discountAmount, total, taxLaborAmount } = record;
	if (discountAmount === 0) return `${discountAmount.toFixed(2)}%`;
	return `${(discountAmount / (discountAmount + total - taxLaborAmount) * 100).toFixed(2)}%`;
}

export const taxPercent = invoice => {
	const { taxLabor = false, taxParts = false, tax = 0 } = invoice
	if (!taxLabor && !taxParts) return `0.00%`
	return  `${(tax * 100).toFixed(2)}%`
}

export const taxTotal = invoice => {
	const {
		taxLabor = false,
		taxParts = false,
		taxLaborAmount = 0,
		taxPartsAmount = 0,
		currency,
	} = invoice
	const total = taxLabor ? taxLaborAmount : 0
	return numberFormatCurrency(
		currency,
		taxParts ? total + taxPartsAmount : total,
	)
}

export const isPaymentOffline = method => method.includes('offline')

export const paymentMethod = method => {
	const _method = isPaymentOffline(method) ? 'offline' : method
	return paymentMethods[_method] || 'other'
}

export const paymentType = method => {
	if (isPaymentOffline(method)) return 'Offline Payment:'
	if (method === 'creditcard') return 'Stripe Verified Payment:'
	return 'Payment'
}

export const getCarInfo = (vehicle = {}) => {
	const { make = '', model = '', color = '' } = vehicle
	return `${make.trim()} ${model.trim()}${color ? `, ${color.trim()}` : ''}`.trim()
}

export const getServiceLines = (vehicle = {}) => {
	const { serviceLines = {} } = vehicle
	const serviceLinesKeys = Object.keys(serviceLines).map(e => serviceLines[e])
	const services = serviceLinesKeys.join(' ')
	return services.trim() ? services.trim() : 'Not listed'
}

export const getService = (vehicle, key) => {
	const { serviceLines = {} } = vehicle
	return serviceLines[key] || ''
}

export const getDocuments = (vehicle) => {
	const docs = []
	if (!vehicle) return docs
	const adas = vehicle.adas

	// Documentation phase documents (pre/setup/post)
	const phases = ['pre', 'setup', 'post']
	for (const phase of phases) {
		const phaseData = adas && adas.documentation && adas.documentation[phase]
		if (phaseData && Array.isArray(phaseData.documents)) {
			for (const doc of phaseData.documents) {
				if (doc && Array.isArray(doc.files)) {
					for (const file of doc.files) {
						if (file && file.fileURL) {
							docs.push({
								fileName: `ADAS ${phase} - ${doc.name || doc.type || 'Document'}`,
								scanType: `ADAS ${phase} - ${doc.name || doc.type || 'Document'}`,
								path: file.fileURL.startsWith('http') ? file.fileURL : `${SERVER_DATA}/${file.fileURL}`,
							})
						}
					}
				}
			}
		}
	}

	// Per-service documents
	if (adas && Array.isArray(adas.services)) {
		for (const svc of adas.services) {
			if (svc && Array.isArray(svc.documents)) {
				for (const doc of svc.documents) {
					if (doc && Array.isArray(doc.files)) {
						for (const file of doc.files) {
							if (file && file.fileURL) {
								docs.push({
									fileName: `ADAS ${doc.name || doc.type || 'Document'} - ${svc.sensorId || 'service'}`,
									scanType: `ADAS ${doc.name || doc.type || 'Document'}`,
									path: file.fileURL.startsWith('http') ? file.fileURL : `${SERVER_DATA}/${file.fileURL}`,
								})
							}
						}
					}
				}
			}

			// Per-service calibration report
			if (svc && svc.calibrationReport && svc.calibrationReport.url) {
				docs.push({
					fileName: `ADAS Calibration Report - ${svc.sensorId || 'service'}`,
					scanType: 'ADAS Calibration Report',
					path: svc.calibrationReport.url.startsWith('http') ? svc.calibrationReport.url : `${SERVER_DATA}/${svc.calibrationReport.url}`,
				})
			}
		}
	}

	// Vehicle-level documents (vehicle.documents[]) — includes BOTH ADAS-tagged and non-ADAS.
	// Without this, "Download All" zips never include any non-ADAS documents.
	if (Array.isArray(vehicle.documents)) {
		for (const doc of vehicle.documents) {
			if (!doc || !Array.isArray(doc.files)) continue
			const isAdas = doc.service === 'adas'
			for (const file of doc.files) {
				if (file && file.fileURL) {
					const path = file.fileURL.startsWith('http') ? file.fileURL : `${SERVER_DATA}/${file.fileURL}`
					docs.push({
						fileName: isAdas ? `ADAS ${doc.name || 'Document'}` : (doc.name || 'Document'),
						scanType: isAdas ? `ADAS ${doc.slot || 'document'}` : (doc.slot || 'document'),
						path,
					})
				}
			}
		}
	}

	return docs
}

export const getPhotosVehicle = vehicle => {
	const { photos = [], videos = [], clas = '' } = vehicle
	const result = [
		// Drop rows with no url BEFORE building getURL. A url-less photo used to
		// produce the string `${SERVER_DATA}/undefined`, which every consumer then
		// tried to fetch, view or zip. See the ADAS loops below, which already
		// guarded on `photo.url` — this is the same rule applied at the top level.
		...photos.filter(photo => photo && (photo.getURL || photo.url)).map((photo) => ({
			...photo,
			name: unifyPanelName(photo.name, '', clas),
			// Prefer the signed URL the API already returned (photo.getURL). The
			// mobiletechrxus objects are not anonymously readable, so a rebuilt
			// SERVER_DATA URL 403s — only the API's signed URL works (matches admin).
			getURL: photo.getURL || (photo.url && photo.url.startsWith('http') ? photo.url : `${SERVER_DATA}/${photo.url}`),
		})),
		...videos.filter(video => video && (video.getURL || video.videoURL)).map((video) => ({
			...video,
			name: unifyPanelName(video.name, '', clas),
			getURL: video.getURL || (video.videoURL && video.videoURL.startsWith('http') ? video.videoURL : `${SERVER_DATA}/${video.videoURL}`),
			url: video.thumbnailURL
		}))
	]

	// ADAS documentation photos (pre/setup/post phases)
	const adas = vehicle.adas
	if (adas && adas.documentation) {
		const phases = ['pre', 'setup', 'post']
		for (const phase of phases) {
			const phaseData = adas.documentation[phase]
			if (phaseData && Array.isArray(phaseData.photos)) {
				for (const photo of phaseData.photos) {
					if (photo && photo.url) {
						const photoUrl = photo.getURL || (photo.url.startsWith('http') ? photo.url : `${SERVER_DATA}/${photo.url}`)
						result.push({
							...photo,
							name: `ADAS ${phase} - ${photo.name || 'Photo'}`,
							getURL: photoUrl,
							url: photo.url,
						})
					}
				}
			}
		}
	}

	// ADAS per-service photos
	if (adas && Array.isArray(adas.services)) {
		for (const svc of adas.services) {
			if (svc && Array.isArray(svc.photos)) {
				for (const photo of svc.photos) {
					if (photo && photo.url) {
						const photoUrl = photo.getURL || (photo.url.startsWith('http') ? photo.url : `${SERVER_DATA}/${photo.url}`)
						result.push({
							...photo,
							name: `ADAS ${svc.sensorId || 'service'} - ${photo.name || 'Photo'}`,
							getURL: photoUrl,
							url: photo.url,
						})
					}
				}
			}
		}
	}

	return result
}

export const getScansVehicle = vehicle => {
	let { scans = [] } = vehicle
	scans = scans
		// `files` itself can be absent, and files.url can be missing — either way
		// there is nothing to open, so drop the row instead of building a bad path.
		.filter(scan => scan != null && scan.files && scan.files.url)
		.map(({scanType, files}) =>
			({ fileName: `${scanType}.pdf`, scanType, path: `${SERVER_DATA}/${files.url}` })
		)
	return scans
}

export const getBlueDriverScansVehicle = (vehicle) => {
	let { blueDriver } = vehicle;
	if (!blueDriver) {
		return [];
	}
	const preScansList = blueDriver.preScans || [];
	const postScansList = blueDriver.postScans || [];
	let bdScans = [];
	// media.url is required, not just a non-empty bdScan — a scan can carry
	// modules/bdScan data with no stored file, which yields an undefined path.
	let preBD = preScansList.filter(scan => scan != null).map(({ scanType, media }) => {
		if (media && media.url && media.bdScan && media.bdScan.length > 0) {
			return { fileName: `BlueDriver PreScan - ${scanType}.pdf`, scanType: `BlueDriver PreScan - ${scanType}`, path: media.url };
		}
	});
	let postBD = postScansList.filter(scan => scan != null).map(({ scanType, media }) => {
		if (media && media.url && media.bdScan && media.bdScan.length > 0) {
			return { fileName: `BlueDriver PostScan - ${scanType}.pdf`, scanType: `BlueDriver PostScan - ${scanType}`, path: media.url };
		}
	});
	bdScans = bdScans.concat(preBD).concat(postBD);
	return bdScans.filter((scan) => scan !== undefined);
};

export const getVideosVehicle = vehicle => {
	const { videos = [] } = vehicle
	return videos
}

export const downloadPhoto = (photoObj, callback) => {
	downloadImage(`${photoObj.getURL}`, photoObj.name, callback);
}

export const downloadZipPhotos = (vehicle, callback) => {
	const photos = getPhotosVehicle(vehicle).map((veh) => {
		return {
			fileName: veh.name, path: `${veh.getURL}`
		}
	})

	const zipName = getCarInfo(vehicle)
	downloadZipFile(zipName, photos)
		.then(() => {
			return callback(true)
		})
		.catch(() => {
			return callback(false)
		})
}

export const getPhotos = (records) => {
	const { vehicles = [] } = records
	let listPhoto = []
	vehicles.forEach(vehicle => {
		const photos = getPhotosVehicle(vehicle).map((photo) => {
			return { fileName: photo.name, path: photo.getURL || `${SERVER_DATA}/${photo.url}` }
		})
		if (photos.length > 0) {
			listPhoto = listPhoto.concat(photos)
		}
	})
	return listPhoto
}

// Downloads all downloadable vehicle files as a zip (excludes external URL links)
export const downloadAllFiles = (vehicle, callback, { includeScans = true } = {}) => {
	// Only include files hosted on our S3 buckets — external URLs are not downloadable
	// Any S3-hosted URL is downloadable (signed URLs are amazonaws.com regardless of
	// virtual-hosted vs path-style); only external OEM links are excluded.
	const isDownloadable = (url) => {
		return url && (url.includes('amazonaws.com') || url.includes(SERVER_DATA))
	}
	const photos = getPhotosVehicle(vehicle)
		.filter(({ getURL }) => isDownloadable(getURL))
		.map(({ name, getURL }) => {
			return { fileName: name, path: `${getURL}`, folder: 'media' }
		})

	const documents = getDocuments(vehicle).map((doc) => ({ ...doc, folder: 'documents' }))
	const scans = includeScans ? getScansVehicle(vehicle).map((s) => ({ ...s, folder: 'documents' })) : []
	const blueDriverScans = includeScans ? getBlueDriverScansVehicle(vehicle).map((s) => ({ ...s, folder: 'documents' })) : []

	const allFiles = [...photos, ...documents, ...scans, ...blueDriverScans]
	if (allFiles.length === 0) {
		return callback(false)
	}
	// Use folders only when both media and documents exist
	const hasMedia = photos.length > 0
	const hasDocs = documents.length > 0 || scans.length > 0 || blueDriverScans.length > 0
	if (!(hasMedia && hasDocs)) {
		allFiles.forEach((f) => { f.folder = undefined })
	}
	downloadZipFile(getCarInfo(vehicle), allFiles)
		.then(() => {
			return callback(true)
		})
		.catch(() => {
			return callback(false)
		})
}

export const downloadPhotosRecord = (records, callback) => {
	const {
		vehicles = [],
		client: { name } = '',
		company: { name: companyName },
		number
	} = records
	let listPhoto = []
	vehicles.forEach(vehicle => {
		const documents = getDocuments(vehicle)
		if (documents.length > 0) {
			listPhoto = listPhoto.concat(documents)
		}
		const photos = getPhotosVehicle(vehicle).map(({ name, url, getURL }) => {
			return { fileName: name, path: getURL || `${SERVER_DATA}/${url}` }
		})
		if (photos.length > 0) {
			listPhoto = listPhoto.concat(photos)
		}
	})
	downloadZipFile(name || `${companyName}${number}`, listPhoto)
		.then(() => {
			return callback(true)
		})
		.catch(() => {
			return callback(false)
		})
}
